import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../../components/common/ConfirmAlertDialog";
import { Lists } from "../../../components/common/Lists";
import TableComponent from "../../../components/common/Tables";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetSubEntitiesQuery } from "../../../hooks/scientific/useGetSubEntitiesQuery";
import { routes } from "../../../routes";
import { SubEntityType } from "../../../types/ScientificModule";
import { Key_subEntities } from "../../../utils/constants";
import { updateSubEntity } from "../../../services/scientific/subEntity";
import { setSubEntitiesData } from "../../../redux/slices/scientific/subEntities";

const columnNamesMap: { [key: string]: string } = {
  description: "Descrição",
  entity: "Entidade",
};

export const SubEntities = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data, status } = useGetSubEntitiesQuery({ PageNumber: currentPage, PageSize: 400 });
  const [deleteItem, setDeleteItem] = useState<SubEntityType | null>(null);

  const mutationUpdate = useMutation(updateSubEntity, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_subEntities);
      toast.success("SubEntidade atualizada com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar a SubEntidade.");
    },
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClickCreate = () => {
    navigate(routes.createSubEntity);
  };

  const handleEdit = (subEntity: SubEntityType) => {
    dispatch(setSubEntitiesData(subEntity));
    navigate(routes.updateSubEntity);
  };

  const handleDelete = (subEntity: SubEntityType) => {
    subEntity.status = false;
    mutationUpdate.mutateAsync(subEntity);
  };

  const openDeleteDialog = (subEntity: SubEntityType) => {
    setDeleteItem(subEntity);
  };

  const confirmDelete = async () => {
    if (deleteItem) {
      await handleDelete(deleteItem);
    }
  };

  const options = data?.items.map((subEntity: SubEntityType) => [
    {
      label: "Editar",
      onClick: () => handleEdit(subEntity),
      icon: <Pencil1Icon />,
    },
    {
      label: "Eliminar",
      onClick: () => openDeleteDialog(subEntity),
      icon: <TrashIcon />,
    },
  ]) || [];

  return (
    <>
      <Lists
        status={status}
        TableComponent={() => (
          <TableComponent
            columns={Object.keys(columnNamesMap)}
            items={data?.items}
            options={options}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            emptyMessage="Ainda não existem subentidades registadas."
          />
        )}
        handleClickCreate={handleClickCreate}
      />
      <ConfirmAlertDialog
        open={!!deleteItem}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title="Esta operação é irreversível"
        description="A subentidade deixará de estar disponível. Pretende continuar?"
        confirmLabel="Eliminar"
        item={deleteItem}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteItem(null)}
      />
    </>
  );
};
